/**
 * The browser half of the upload rules (SCRUM-33).
 *
 * These mirror `api/src/upload.rs` and the two chat ceilings in
 * `api/src/config.rs`. They exist to refuse an obviously wrong file before its
 * bytes leave the browser, and to say why. The API checks again from the
 * contents rather than the name, and that is the check that counts.
 */

/** CHAT_ATTACHMENT_MAX_BYTES. */
export const MAX_ATTACHMENT_BYTES = 20 * 1024 * 1024;

/** CHAT_VIDEO_MAX_BYTES. */
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

const IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
];

const VIDEO_TYPES = ["video/mp4", "video/quicktime"];

const DOCUMENT_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/vnd.oasis.opendocument.presentation",
];

/** Extensions for files whose browser-reported type is unreliable, such as HEIC. */
const EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "heic",
  "heif",
  "mp4",
  "m4v",
  "mov",
  "qt",
  "pdf",
  "docx",
  "xlsx",
  "pptx",
  "odt",
  "ods",
  "odp",
];

export const ACCEPT_ATTRIBUTE = [
  ...IMAGE_TYPES,
  ...VIDEO_TYPES,
  ...DOCUMENT_TYPES,
  ".heic",
  ".heif",
].join(",");

function extensionOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot + 1).toLowerCase();
}

export function isVideo(file: File): boolean {
  return (
    VIDEO_TYPES.includes(file.type) ||
    ["mp4", "m4v", "mov", "qt"].includes(extensionOf(file.name))
  );
}

/** A short human label for a file, used beside its name in the composer. */
export function describeFile(file: File): string {
  if (isVideo(file)) return "Video";
  if (IMAGE_TYPES.includes(file.type) || ["heic", "heif"].includes(extensionOf(file.name))) {
    return "Image";
  }
  return "Document";
}

export function attachmentSizeLabel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const megabytes = bytes / (1024 * 1024);
  return `${megabytes >= 10 ? Math.round(megabytes) : megabytes.toFixed(1)} MB`;
}

/**
 * Returns a message naming what is wrong, or `null` when the file looks
 * acceptable. "Upload failed" teaches a relocation coordinator nothing, so
 * every refusal says what is allowed instead.
 */
export function isAcceptedFile(file: File): string | null {
  if (file.size === 0) {
    return `${file.name} is empty.`;
  }

  const extension = extensionOf(file.name);
  const knownType =
    IMAGE_TYPES.includes(file.type) ||
    VIDEO_TYPES.includes(file.type) ||
    DOCUMENT_TYPES.includes(file.type);

  if (!knownType && !EXTENSIONS.includes(extension)) {
    return "You can send images (JPEG, PNG, WebP, GIF, HEIC), video (MP4, MOV), PDFs, and Office or OpenDocument files.";
  }

  const limit = isVideo(file) ? MAX_VIDEO_BYTES : MAX_ATTACHMENT_BYTES;
  if (file.size > limit) {
    return `${file.name} is ${attachmentSizeLabel(file.size)}. ${
      isVideo(file) ? "Videos" : "Images and documents"
    } are limited to ${attachmentSizeLabel(limit)}.`;
  }

  return null;
}
